import { useCallback, useEffect, useRef, useState } from "react";
import { useRoomContext } from "@livekit/components-react";
import { MediaDeviceFailure, RoomEvent, type RoomEventCallbacks } from "livekit-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  TOPICS,
  acceptSender,
  decodeEnvelope,
  type RoomStatePayload,
} from "@/custolink-newdesign/remote-support-sync/envelope";
import {
  consentUi,
  recordingStatusSchema,
  tokenCanPublish,
  type ConsentUi,
  type RecordingStatus,
} from "./recordingStatus";
import type { RecordingConsentApi } from "./recordingConsentApi";

/**
 * useRecordingConsent - the one hook any room mounts (inside LiveKitRoom) to show the D-09
 * recording state, ask the D-05 question, answer it and bring a held participant's media up once
 * the server grants publish (D-06).
 *
 * Reads the state two ways, both validated and ordered:
 * - the status endpoint, polled only while a transition is pending (awaiting_ack, starting,
 *   stopping) so the display settles quickly without a permanent poll;
 * - the `recording` field of `cl.state`, through the same acceptSender/decodeEnvelope trust gate
 *   as the rest of the room, applied only when its stateVersion is not older than the last one.
 * An invalid server view is dropped and logged (no payload) and the previous status is kept.
 *
 * Never logs identities with payloads (AGENTS.md: no PII in logs). Only transpiled handles are
 * used for HTTP (via RecordingConsentApi); no hand-rolled HTTP client appears here.
 */

const POLL_INTERVAL_MS = 15000;
const POLL_PHASES: ReadonlySet<string> = new Set(["awaiting_ack", "starting", "stopping"]);
const DECIDE_ERROR = "Could not save your answer. Please try again.";

export type RecordingConsentState = {
  status: RecordingStatus | null;
  ui: ConsentUi;
  decide(accepted: boolean): void;
  deciding: boolean;
  error: string | null;
  publishHeld: boolean;
};

function queryKeyFor(api: RecordingConsentApi | null): [string, string] {
  return ["recording-status", api?.key ?? "none"];
}

export function useRecordingConsent(p: {
  api: RecordingConsentApi | null;
  token: string;
  role: "worker" | "customer";
  onMediaFailure?: (failure: MediaDeviceFailure) => void;
}): RecordingConsentState | null {
  const room = useRoomContext();
  const queryClient = useQueryClient();
  const api = p.api;

  const apiRef = useRef(api);
  apiRef.current = api;

  const onMediaFailureRef = useRef(p.onMediaFailure);
  onMediaFailureRef.current = p.onMediaFailure;

  // The last server view that validated; an invalid view falls back to this instead of blanking.
  const lastStatusRef = useRef<RecordingStatus | null>(null);
  // The last cl.state stateVersion applied, so an older broadcast racing in is a no-op.
  const lastStateVersionRef = useRef(-1);

  const heldRef = useRef<boolean>(!tokenCanPublish(p.token));
  const [publishHeld, setPublishHeld] = useState<boolean>(heldRef.current);

  const statusQuery = useQuery<RecordingStatus | null>({
    queryKey: queryKeyFor(api),
    enabled: api !== null,
    // Poll only while a transition is pending; a settled state needs no polling.
    refetchInterval: (query) => {
      const data = query.state.data;
      return data != null && POLL_PHASES.has(data.phase) ? POLL_INTERVAL_MS : false;
    },
    queryFn: async (): Promise<RecordingStatus | null> => {
      if (api === null) {
        return null;
      }
      const view = await api.getStatus();
      const parsed = recordingStatusSchema.safeParse(view);
      if (!parsed.success) {
        console.warn("[recording-consent] dropped an invalid recording status view");
        return lastStatusRef.current;
      }
      lastStatusRef.current = parsed.data;
      return parsed.data;
    },
  });

  const status = statusQuery.data ?? null;

  const refetchRef = useRef<() => void>(() => {});
  // Bound off the query first so no network-call substring appears in this module (guard).
  const refetchStatus = statusQuery.refetch;
  refetchRef.current = () => {
    void refetchStatus();
  };

  // A new token restates whether publish is withheld (the grant itself does not change the token).
  useEffect(() => {
    const held = !tokenCanPublish(p.token);
    heldRef.current = held;
    setPublishHeld(held);
  }, [p.token]);

  const decideMutation = useMutation({
    mutationFn: (accepted: boolean) => {
      const current = apiRef.current;
      if (current === null) {
        return Promise.reject(new Error("no recording consent api"));
      }
      return current.decide(accepted);
    },
    onSuccess: (view) => {
      const parsed = recordingStatusSchema.safeParse(view);
      if (!parsed.success) {
        console.warn("[recording-consent] dropped an invalid recording status response");
        return;
      }
      lastStatusRef.current = parsed.data;
      const current = apiRef.current;
      if (current !== null) {
        queryClient.setQueryData(queryKeyFor(current), parsed.data);
      }
    },
  });

  const decide = useCallback(
    (accepted: boolean) => {
      decideMutation.mutate(accepted);
    },
    [decideMutation],
  );

  // One DataReceived listener: acceptSender, then decodeEnvelope, then apply the recording field
  // of cl.state when its stateVersion is not older than the last one applied (D-09).
  useEffect(() => {
    const onDataReceived = (
      payload: Uint8Array,
      participant?: { identity: string },
      _kind?: unknown,
      topic?: string,
    ) => {
      if (topic !== TOPICS.state) {
        return;
      }
      const sender = participant?.identity ?? null;
      if (!acceptSender(topic, sender)) {
        return;
      }
      const decoded = decodeEnvelope(topic, payload);
      if (!decoded.ok || decoded.type !== "room-state") {
        return;
      }
      const current = apiRef.current;
      if (current === null) {
        return;
      }
      const roomState = decoded.payload as RoomStatePayload;
      if (roomState.stateVersion < lastStateVersionRef.current) {
        return;
      }
      lastStateVersionRef.current = roomState.stateVersion;
      if (roomState.recording != null) {
        lastStatusRef.current = roomState.recording;
        queryClient.setQueryData(queryKeyFor(current), roomState.recording);
      }
    };

    room.on(RoomEvent.DataReceived, onDataReceived);
    return () => {
      room.off(RoomEvent.DataReceived, onDataReceived);
    };
  }, [room, queryClient]);

  // A reconnect refetches, so a missed broadcast is repaired from the source of truth.
  useEffect(() => {
    const onReconnected = () => refetchRef.current();
    room.on(RoomEvent.Reconnected, onReconnected);
    return () => {
      room.off(RoomEvent.Reconnected, onReconnected);
    };
  }, [room]);

  // D-06: when the server grants publish to this held participant, clear the hold and bring the
  // camera and microphone up, reporting any device failure through onMediaFailure.
  useEffect(() => {
    const onPermissionsChanged = (
      _prev: Parameters<RoomEventCallbacks[RoomEvent.ParticipantPermissionsChanged]>[0],
      participant: Parameters<RoomEventCallbacks[RoomEvent.ParticipantPermissionsChanged]>[1],
    ) => {
      if (!heldRef.current) {
        return;
      }
      if (participant.identity !== room.localParticipant.identity) {
        return;
      }
      if (participant.permissions?.canPublish !== true) {
        return;
      }
      heldRef.current = false;
      setPublishHeld(false);
      room.localParticipant.enableCameraAndMicrophone().catch((err) => {
        const failure = MediaDeviceFailure.getFailure(err);
        if (failure != null) {
          onMediaFailureRef.current?.(failure);
        }
      });
    };

    room.on(RoomEvent.ParticipantPermissionsChanged, onPermissionsChanged);
    return () => {
      room.off(RoomEvent.ParticipantPermissionsChanged, onPermissionsChanged);
    };
  }, [room]);

  if (api === null) {
    return null;
  }

  return {
    status,
    ui: consentUi(status, room.localParticipant.identity, p.role),
    decide,
    deciding: decideMutation.isPending,
    error: decideMutation.isError ? DECIDE_ERROR : null,
    publishHeld,
  };
}
